import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Marketing Pages — lazy loaded for route-based code splitting
const HomePage = React.lazy(() => import('./pages/marketing/HomePage'));
const PlatformPage = React.lazy(() => import('./pages/marketing/PlatformPage'));
const FeaturesPage = React.lazy(() => import('./pages/marketing/FeaturesPage'));
const SolutionsPage = React.lazy(() => import('./pages/marketing/SolutionsPage'));
const EnterprisePage = React.lazy(() => import('./pages/marketing/EnterprisePage'));
const WhiteLabelPage = React.lazy(() => import('./pages/marketing/WhiteLabelPage'));
const AboutPage = React.lazy(() => import('./pages/marketing/AboutPage'));
const ResourcesPage = React.lazy(() => import('./pages/marketing/ResourcesPage'));
const ContactPage = React.lazy(() => import('./pages/marketing/ContactPage'));
const InvestorDeckPage = React.lazy(() => import('./pages/marketing/InvestorDeckPage'));
const ResourceDetailPage = React.lazy(() => import('./pages/marketing/ResourceDetailPage'));
const DfmBlogDetailPage = React.lazy(() => import('./pages/marketing/DfmBlogDetailPage'));
const ConstructionLandingPage = React.lazy(() => import('./pages/marketing/ConstructionLandingPage'));
const AgricultureLandingPage = React.lazy(() => import('./pages/marketing/AgricultureLandingPage'));
const InspectionLandingPage = React.lazy(() => import('./pages/marketing/InspectionLandingPage'));
const PublicSafetyLandingPage = React.lazy(() => import('./pages/marketing/PublicSafetyLandingPage'));
const ROICalculatorPage = React.lazy(() => import('./pages/marketing/ROICalculatorPage'));

const NotFoundPage = React.lazy(() => import('./pages/marketing/NotFoundPage'));

// Existing Pages — lazy loaded
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const SupportLandingPage = React.lazy(() => import('./pages/SupportLandingPage'));

// Redirects to an external URL
function ExternalRedirect({ to }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

// Site-wide default head tags. Page-level <Helmet> blocks override these;
// pages without their own get a self-referencing canonical instead of the
// homepage's (which is what the static index.html shell would otherwise leave).
const SITE_URL = 'https://dronefleetmanagement.com';
const DEFAULT_TITLE = 'Drone Fleet Management Software | UAV Operations Platform';
const DEFAULT_DESCRIPTION = 'Enterprise UAV fleet management software. Plan missions, track drones in real-time, ensure airspace compliance — all from one platform.';
const DEFAULT_IMAGE = `${SITE_URL}/assets/images/og-image.webp`;

function SeoDefaults() {
  const { pathname } = useLocation();
  const url = SITE_URL + (pathname === '/' ? '/' : pathname.replace(/\/+$/, ''));
  return (
    <Helmet>
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Drone Fleet Management" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@DroneFleetMgmt" />
      <meta name="twitter:title" content={DEFAULT_TITLE} />
      <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
    </Helmet>
  );
}

// Google Analytics page tracking component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-H0TPFBSJ4L', {
        page_path: location.pathname + location.search
      });
    }
  }, [location]);

  return null;
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Don't scroll if there's a hash (anchor link)
    if (hash) {
      // Let the browser handle anchor scrolling
      const element = document.getElementById(hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      // Scroll to top instantly on route change
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <SeoDefaults />
      <AnalyticsTracker />
      <ScrollToTop />
      <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
        <Routes>
          {/* Marketing Website Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/enterprise" element={<EnterprisePage />} />
          <Route path="/enterprise/white-label" element={<WhiteLabelPage />} />
          <Route path="/pricing" element={<ExternalRedirect to="https://portal.dronefleetpro.com/register" />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/:slug" element={<ResourceDetailPage />} />
          <Route path="/blog/:slug" element={<DfmBlogDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/demo" element={<Navigate to="/contact" />} />
          <Route path="/marketing/investor-deck" element={<InvestorDeckPage />} />

          {/* New standalone pages */}
          <Route path="/roi-calculator" element={<ROICalculatorPage />} />
          <Route path="/white-label" element={<Navigate to="/enterprise/white-label" />} />

          {/* Vertical Landing Pages */}
          <Route path="/for-construction" element={<ConstructionLandingPage />} />
          <Route path="/for-agriculture" element={<AgricultureLandingPage />} />
          <Route path="/for-inspection" element={<InspectionLandingPage />} />
          <Route path="/for-public-safety" element={<PublicSafetyLandingPage />} />

          {/* Existing Routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/support-center" element={<SupportLandingPage />} />

          {/* Legal Pages (redirect to existing or placeholder) */}
          <Route path="/terms" element={<Navigate to="/privacy-policy" />} />
          <Route path="/cookies" element={<Navigate to="/privacy-policy" />} />
          <Route path="/gdpr" element={<Navigate to="/privacy-policy" />} />

          {/* Catch-all: real 404 page (noindex) instead of a soft-404 redirect to home */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
